import { populate } from '~/lib/category/populate';
import type { CategorySchema } from '~/types/schema';
import { PostTypes, ContentTypes } from '~/types/adamTypes';
import type { NomadDB } from '~/db/NomadDB';

export type CategoryHelper = {
  getContentType: (category: CategorySchema) => ContentTypes;
  hasCategories: (category: CategorySchema) => boolean;
  hasOnlyCategoryWithType: (
    category: CategorySchema,
    type: ContentTypes
  ) => boolean;
  hasOnlyCategoryWithTypes: (
    category: CategorySchema,
    types: ContentTypes[]
  ) => boolean;
  hasDifferentCategories: (category: CategorySchema) => boolean;
  hasOnlyOneCategory: (category: CategorySchema) => boolean;
  hasOneOfThisCategory: (
    category: CategorySchema,
    types: ContentTypes[]
  ) => boolean;
  hasChildren: (category: CategorySchema) => boolean;
  hasOnlyAPost: (category: CategorySchema) => boolean;
  hasOnlyPosts: (category: CategorySchema) => boolean;
  hasOnlyPdfs: (category: CategorySchema) => boolean;
  hasOnlyPostsWithType: (category: CategorySchema, type: PostTypes) => boolean;
  hasOnlyExercises: (category: CategorySchema) => boolean;
  hasOnlyAnExercise: (category: CategorySchema) => boolean;
  hasExercisesAndCourses: (category: CategorySchema) => boolean;
  hasOnlyACategory: (category: CategorySchema) => boolean;
  firstPostIs: (category: CategorySchema, type: PostTypes) => boolean;
  ensureHasNotOnlyACategory: (
    $db: NomadDB,
    category: CategorySchema
  ) => Promise<CategorySchema>;
  ensureHasBeenPopulated: (
    $db: NomadDB,
    category: CategorySchema
  ) => Promise<CategorySchema>;
  hasExercises: (category: CategorySchema) => boolean;
};

export const categoryHelper = (): CategoryHelper => {
  const ensureHasBeenPopulated = async (
    $db: NomadDB,
    category: CategorySchema
  ): Promise<CategorySchema> => {
    if (
      category.children &&
      category.children.length &&
      !category.children[0].contentType
    ) {
      const populatedData = await populate(
        $db,
        category,
        category.libraryBookId,
        false
      );

      category = populatedData.category;
    }
    return category;
  };

  const ensureHasNotOnlyACategory = async (
    $db: NomadDB,
    category: CategorySchema,
    depth: number = 0
  ): Promise<CategorySchema> => {
    await ensureHasBeenPopulated($db, category);

    if (hasOnlyACategory(category)) {
      category = category.children[0];
      if (depth < 3) {
        return await ensureHasNotOnlyACategory($db, category, depth++);
      }
    }
    return category;
  };

  const hasOnlyOneCategory = (category: CategorySchema) => {
    return category.children.length === 1;
  };

  const getContentType = (category: CategorySchema): ContentTypes => {
    return category.contentType;
  };

  const getChildrenTypes = (category: CategorySchema) => {
    if (!category.children) return [];
    return category.children.map((child) => child.contentType);
  };

  const hasCategories = (category: CategorySchema) => {
    const bool = category.children?.length > 1;
    return bool;
  };

  const hasOnlyCategoryWithType = (
    category: CategorySchema,
    contentType: ContentTypes
  ) => {
    if (!category.children || category.children.length === 0) return false;
    return !category.children.some(
      (child) => child.contentType !== contentType
    );
  };

  const hasOnlyCategoryWithTypes = (
    category: CategorySchema,
    contentTypes: ContentTypes[]
  ) => {
    if (!category.children || category.children.length === 0) return false;
    const bool = !category.children.find((child) => {
      return !contentTypes.includes(child.contentType);
    });
    return bool;
  };

  const hasDifferentCategories = (category: CategorySchema) => {
    const childrenTypes = getChildrenTypes(category);
    const types = new Map<string, boolean>();
    const hasMultiple = childrenTypes.some((type) => {
      types.set(type, true);
      return types.size > 1;
    });
    return hasMultiple;
  };

  const hasOneOfThisCategory = (
    category: CategorySchema,
    types: ContentTypes[]
  ): boolean => {
    const childrenTypes = getChildrenTypes(category);
    const bool =
      types.filter((type) => childrenTypes.includes(type)).length > 0;
    return bool;
  };

  const hasChildren = (category: CategorySchema) => {
    if (category && category.children && category.children.length > 0) {
      return true;
    }
    return false;
  };

  const hasOnlyACategory = (category: CategorySchema) => {
    return (
      category.children &&
      category.children.length === 1 &&
      !category?.posts?.length &&
      !category?.exercises?.length
    );
  };

  const hasOnlyPosts = (category: CategorySchema) => {
    return (
      !hasChildren(category) &&
      category.posts &&
      category.posts.length > 0 &&
      !category?.exercises?.length
    );
  };

  const hasOnlyAPost = (category: CategorySchema) => {
    return (
      !hasChildren(category) &&
      category.posts &&
      category.posts.length === 1 &&
      !category?.exercises?.length
    );
  };

  const hasOnlyPdfs = (category: CategorySchema) => {
    return (
      hasOnlyPostsWithType(category, PostTypes.ContentPDF) ||
      hasOnlyPostsWithType(category, PostTypes.CorrectionPDF) ||
      hasOnlyPostsWithType(category, PostTypes.PracticeSubjectPDF) ||
      hasOnlyPostsWithType(category, PostTypes.PracticeCorrectionPDF) ||
      hasOnlyPostsWithType(category, PostTypes.PracticeToolkitPDF)
    );
  };

  const hasOnlyExercises = (category: CategorySchema) => {
    return (
      !hasChildren(category) &&
      category.posts &&
      category.posts.length === 0 &&
      category.exercises &&
      category.exercises.length > 0
    );
  };

  const hasExercises = (category: CategorySchema) => {
    return category.exercises && category.exercises.length > 0;
  };

  const hasOnlyAnExercise = (category: CategorySchema) => {
    return (
      !hasChildren(category) &&
      category.posts &&
      category.posts.length === 0 &&
      category.exercises.length === 1
    );
  };

  const hasExercisesAndCourses = (category: CategorySchema) =>
    category.posts.length > 0 && category.quizzes.length > 0;

  const hasOnlyPostsWithType = (category: CategorySchema, postType) => {
    if (!category?.posts?.length) return false;
    return !category.posts.some((child) => child.contentType !== postType);
  };

  const firstPostIs = (category: CategorySchema, postType) => {
    if (!category?.posts?.length) return false;
    return category.posts[0] === postType;
  };

  return {
    getContentType,
    hasCategories,
    hasOnlyCategoryWithType,
    hasOnlyCategoryWithTypes,
    hasDifferentCategories,
    hasOneOfThisCategory,
    hasOnlyOneCategory,
    hasChildren,
    hasOnlyAPost,
    hasOnlyPosts,
    hasOnlyPdfs,
    hasOnlyPostsWithType,
    firstPostIs,
    hasOnlyAnExercise,
    hasOnlyExercises,
    hasExercisesAndCourses,
    hasOnlyACategory,
    ensureHasNotOnlyACategory,
    ensureHasBeenPopulated,
    hasExercises,
  };
};
